import { Infinity, UserIcon } from 'lucide-react'
import React, { useState} from 'react'
import { useTranslation } from 'react-i18next'
import { API_URL } from '../../services/apiService'
import {formatFullName} from '../../utils/personUtils'

const toOrdinal = (num, t, gender) => {
    const femaleOrdinals = {
        2: t('ordinal.female.second'),
        3: t('ordinal.female.third'),
        4: t('ordinal.female.fourth'),
        5: t('ordinal.female.fifth'),
    }

    const maleOrdinals = {
        2: t('ordinal.male.second'),
        3: t('ordinal.male.third'),
        4: t('ordinal.male.fourth'),
        5: t('ordinal.male.fifth'),
    }

    return gender === 'F' ? femaleOrdinals[num] : maleOrdinals[num] || `#${num}`
}

const PersonCard = ({ person, spouseIndex, onClick, nodeDatum }) => {
    const {t, i18n} = useTranslation()
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

  return (
    <div
      className="flex flex-col items-center cursor-pointer"
      onClick={(e) => {
        e.stopPropagation()
        onClick(nodeDatum)
      }}
    >
      <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-xl overflow-hidden border-2 border-[#e6dfd4] mb-2">
          {person.image && !imageError ? (
              <img
                  crossOrigin="use-credentials"
                  src={`${API_URL}/image/download/${encodeURIComponent(person.image)}`}
                  alt={`${formatFullName(person, i18n['language'])}`}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
              />
          ) : (
              <div className="w-full h-full flex items-center justify-center">
                  <UserIcon className="w-8 h-8 text-[#937c5d]"/>
              </div>
          )}
      </div>

      <div className="text-[#4a3f30] font-bold text-sm md:text-base">
          {formatFullName(person, i18n['language'])}

      </div>

        {spouseIndex !== null && (
            <div className="text-xs text-[#6e5c4a] mt-1">
                {person.gender === 'F'
                    ? spouseIndex === 1
                        ? t('treeView.hisWife')
                        : t('treeView.hisWifeOrdinal', {
                            ordinal: toOrdinal(spouseIndex, t, 'F'),
                        })
                    : spouseIndex === 1
                        ? t('treeView.herHusband')
                        : t('treeView.herHusbandOrdinal', {
                            ordinal: toOrdinal(spouseIndex, t, 'M'),
                        })}
            </div>
        )}
    </div>
  )
}

export function renderCustomNode({ nodeDatum, API_URL, onClick }) {
  const isMarriage = nodeDatum.attributes?.isMarriage
  const hasChildren = nodeDatum.children?.length > 0

  if (isMarriage && nodeDatum.spouses) {
    const primarySpouse = nodeDatum.spouses.find((sp) => sp.isPrimary)
    const nonPrimarySpouses = nodeDatum.spouses.filter((sp) => !sp.isPrimary)
    nonPrimarySpouses.reverse()
    const spouseCount = nonPrimarySpouses.length

    const totalSlots = spouseCount + 1
    const containerWidth = 200 * totalSlots
    const containerX = -(containerWidth / 2)

    const InfinitySeparator = () => (
      <div className="flex items-center justify-center group">
        <Infinity className="w-6 h-6 text-[#c9a66c] transform group-hover:scale-110 transition-all duration-300" />
      </div>
    )

    const renderSpouseLayout = () => {
      if (spouseCount === 1) {
        const [theSpouse] = nonPrimarySpouses
        return (
          <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md border border-[#e6dfd4] hover:shadow-lg transition-all duration-300">
            <PersonCard
              person={primarySpouse}
              spouseIndex={null}
              onClick={onClick}
              nodeDatum={nodeDatum}
              API_URL={API_URL}
            />
            <InfinitySeparator />
            <PersonCard
              person={theSpouse}
              spouseIndex={1}
              onClick={onClick}
              nodeDatum={nodeDatum}
              API_URL={API_URL}
            />
          </div>
        )
      } else if (spouseCount === 2) {
        const [firstSpouse, secondSpouse] = nonPrimarySpouses
        return (
          <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md border border-[#e6dfd4] hover:shadow-lg transition-all duration-300">
            <PersonCard
              person={firstSpouse}
              spouseIndex={1}
              onClick={onClick}
              nodeDatum={nodeDatum}
              API_URL={API_URL}
            />
            <InfinitySeparator />
            <PersonCard
              person={primarySpouse}
              spouseIndex={null}
              onClick={onClick}
              nodeDatum={nodeDatum}
              API_URL={API_URL}
            />
            <InfinitySeparator />
            <PersonCard
              person={secondSpouse}
              spouseIndex={2}
              onClick={onClick}
              nodeDatum={nodeDatum}
              API_URL={API_URL}
            />
          </div>
        )
      } else {
        return (
          <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md border border-[#e6dfd4] hover:shadow-lg transition-all duration-300">
            <PersonCard
              person={primarySpouse}
              spouseIndex={null}
              onClick={onClick}
              nodeDatum={nodeDatum}
              API_URL={API_URL}
            />
            {nonPrimarySpouses.map((sp, idx) => (
              <React.Fragment key={sp.gedId}>
                <InfinitySeparator />
                <PersonCard
                  person={sp}
                  spouseIndex={idx + 1}
                  onClick={onClick}
                  nodeDatum={nodeDatum}
                  API_URL={API_URL}
                />
              </React.Fragment>
            ))}
          </div>
        )
      }
    }

    return (
      <foreignObject
        x={containerX}
        y="-70"
        width={containerWidth}
        height="140"
        style={{ overflow: 'visible' }}
      >
        {renderSpouseLayout()}
      </foreignObject>
    )
  }

  return (
    <foreignObject
      x="-100"
      y="-70"
      width="200"
      height="140"
      style={{ overflow: 'visible' }}
    >
      <div
        className={`p-4 bg-white rounded-xl shadow-md border border-[#e6dfd4] ${
          hasChildren ? 'hover:shadow-lg cursor-pointer' : ''
        }`}
      >
        <PersonCard
          person={nodeDatum}
          spouseIndex={null}
          onClick={onClick}
          nodeDatum={nodeDatum}
          API_URL={API_URL}
        />
      </div>
    </foreignObject>
  )
}
